import styles from '@/styles/ChatWindow.module.scss';
import { FormOutlined } from '@ant-design/icons';
import { Dropdown, Menu, Switch } from 'antd';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import ModalCreateInquiry from '../Modals/ModalCreateInquiry';
import { InquiryModal } from '@/utils/enums';
import ModalEditInquiry from '../Modals/ModalEditInQuiry';
import { getInquiryMenu } from '@/components/menu';
import ModalDeleteInquiry from '../Modals/ModalDeleteInquiry';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '@/context';

export const ChatMenu = ({
  inquiries,
  setInquiries,
  selectedInquiry,
  setSelectedInquiry,
  selectedInquiryId,
  setSelectedInquiryId,
  collapsed,
  setCollapsed,
  orgView,
  setOrgView,
}: any) => {
  const { t } = useTranslation();
  const { user } = useAppContext();
  const isAdmin = user?.is_admin === true;
  const [searchParams] = useSearchParams();
  const [autoSelected, setAutoSelected] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<InquiryModal | null>(null);

  useEffect(() => {
    if (searchParams) {
      const inquiryIdParam = searchParams.get('inquiry_id');
      if (inquiryIdParam != null) {
        setSelectedInquiryId(inquiryIdParam);
      } else {
        setAutoSelected(true);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (!searchParams || !selectedInquiryId) return;
  }, [selectedInquiryId, searchParams]);

  useEffect(() => {
    if (autoSelected && !selectedInquiryId && inquiries.length > 0) {
      setSelectedInquiryId(inquiries[0].id);
      setSelectedInquiry(inquiries[0]);
    }
  }, [autoSelected, inquiries]);

  const addInquiry = (newInquiry: any) => {
    setInquiries((prev: any) => [newInquiry, ...prev]);
    setSelectedInquiryId(newInquiry?.id);
    setSelectedInquiry(newInquiry);
    setOpenModal(null);
  };

  const renamePersonalInquiry = (editedInquiry: any) => {
    setInquiries((origin: any[]) => {
      const listInquiry = origin.filter(
        (item: any) => item.id != editedInquiry.id,
      );
      return [editedInquiry, ...listInquiry];
    });
  };

  const deletePersonalInquiry = (deletedInquiryId: string) => {
    const newInquiries = inquiries.filter(
      (item: any) => item.id != deletedInquiryId,
    );
    setInquiries(newInquiries);
    setSelectedInquiryId(newInquiries.length > 0 ? newInquiries[0]?.id : null);
  };

  return (
    <div className={styles.qaMenu}>
      <div
        style={{
          display: 'flex',
          background: 'white',
          textAlign: 'center',
          padding: `${collapsed ? '20px 10px' : '20px'}`,
          borderRadius: collapsed ? '8px' : 0,
          fontWeight: 'bold',
          justifyContent: 'space-between',
          flexDirection: collapsed ? 'column-reverse' : 'row',
          gap: 20,
        }}
      >
        <div>
          {!collapsed ? (
            <div
              style={{
                display: 'flex',
                gap: 10,
                cursor: 'pointer',
              }}
              onClick={() => setOpenModal(InquiryModal.ADD)}
            >
              <FormOutlined style={{ fontSize: '20px' }} />
              <span style={{ fontSize: '16px' }}>{t('home.chat.newQA')}</span>
            </div>
          ) : (
            <FormOutlined
              style={{
                fontSize: '20px',
                color: '#00000073',
              }}
              onClick={() => setOpenModal(InquiryModal.ADD)}
            />
          )}
        </div>
        <img
          alt=""
          width={20}
          height={20}
          src={'/icons/collapsed.svg'}
          style={{
            display: 'flex',
            width: collapsed ? '100%' : '',
            textAlign: 'center',
            transform: collapsed ? 'scaleX(-1)' : 'scaleX(1)',
            cursor: 'pointer',
          }}
          onClick={() => setCollapsed(!collapsed)}
        />
      </div>
      {!collapsed && (
        <div style={{ background: 'white', flex: 1 }}>
          {/* Organization View toggle — visible only to admins */}
          {isAdmin && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 20px 12px 28px',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <Switch
                id="org-view-toggle"
                size="small"
                checked={orgView}
                onChange={(checked: boolean) => setOrgView(checked)}
              />
              <span style={{ fontSize: '13px', color: '#595959', fontWeight: 500 }}>
                {t('home.chat.orgView')}
              </span>
            </div>
          )}

          <b style={{ paddingLeft: '28px', fontSize: '16px', display: 'block', paddingTop: '10px' }}>
            {t('home.chat.recentQA')}
          </b>
          <div style={{ fontSize: '16px' }} className={styles.menuContainer}>
            <Menu
              mode="inline"
              className={styles.chatMenu}
              style={{ fontSize: '16px', border: 'none' }}
              selectedKeys={
                selectedInquiryId ? [String(selectedInquiryId)] : []
              }
              onSelect={(info: any) => {
                const inquiryKey = info.key;
                setSelectedInquiryId(inquiryKey);
                const clickedInquiry = inquiries.find(
                  (element: any) => element.id === inquiryKey,
                );
                if (clickedInquiry) {
                  setSelectedInquiry(clickedInquiry);
                }
              }}
              items={inquiries?.map((inquiry: any) => {
                const isSelected =
                  String(inquiry.id) === String(selectedInquiryId);
                // In org view, only show edit/delete for chats the current user owns.
                const canEdit = !orgView || inquiry.is_own === true;
                return {
                  key: String(inquiry.id),
                  icon: (
                    <FormOutlined
                      style={{
                        fontSize: '20px',
                        color: isSelected ? '#1890ff' : '#00000073',
                      }}
                    />
                  ),
                  label: (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        justifyContent: 'space-between',
                        overflow: 'hidden',
                        gap: '20px',
                      }}
                    >
                      <div style={{ flex: '1 1 80%', overflow: 'hidden' }}>
                        <span
                          style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'block',
                            fontWeight: isSelected ? '600' : 'normal',
                            color: isSelected ? '#1890ff' : 'inherit',
                          }}
                          title={inquiry?.name}
                        >
                          {inquiry?.name}
                        </span>
                        {orgView && inquiry.owner_name && (
                          <span
                            style={{
                              fontSize: '11px',
                              color: '#8c8c8c',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: 'block',
                            }}
                            title={inquiry.owner_name}
                          >
                            {t('home.chat.createdBy', { name: inquiry.owner_name })}
                          </span>
                        )}
                      </div>
                      {canEdit && (
                        <Dropdown
                          menu={getInquiryMenu(t, setOpenModal)}
                          trigger={['click']}
                          overlayStyle={{ flex: '1 1 20%' }}
                        >
                          <img
                            alt=""
                            width={24}
                            height={24}
                            src={'/icons/more.svg'}
                            onClick={(e: any) => {
                              e.stopPropagation();
                              setSelectedInquiry(inquiry);
                            }}
                          />
                        </Dropdown>
                      )}
                    </div>
                  ),
                };
              })}
            />
          </div>
        </div>
      )}
      <ModalCreateInquiry
        open={openModal == InquiryModal.ADD}
        onOk={addInquiry}
        onCancel={() => setOpenModal(null)}
        projectId={null}
      />
      <ModalEditInquiry
        inquiry={selectedInquiry}
        open={openModal == InquiryModal.EDIT}
        onCancel={() => setOpenModal(null)}
        updateInquiry={renamePersonalInquiry}
      />
      <ModalDeleteInquiry
        inquiry={selectedInquiry}
        open={openModal == InquiryModal.DELETE}
        onCancel={() => setOpenModal(null)}
        removeInquiry={deletePersonalInquiry}
      />
    </div>
  );
};
